import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { Badge } from '@/marketing/components/ui/badge';
import { Sheet, SheetContent } from '@/marketing/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/marketing/components/ui/table';
import type { InfluencerVoucher, VoucherPerformanceOrder } from '@/marketing/models/voucher';
import { VOUCHER_QUERIES } from '@/marketing/infrastructure/influencer/voucher/utils/queries';
import { formatCurrency, formatDate, parseAmount } from '@/marketing/lib/formatters';
import { buildVoucherChannelClickRows } from '@/marketing/lib/voucherChannelClicks';
import {
  resolveVoucherOrderDisplayStatus,
  VOUCHER_ORDER_STATUS_LABELS,
  type VoucherOrderDisplayStatus,
} from '@/marketing/lib/voucherOrderStatus';

const ORDER_STATUS_STYLES: Record<VoucherOrderDisplayStatus, string> = {
  paid: 'tw-bg-green-100 tw-text-green-700 hover:tw-bg-green-100',
  confirmed: 'tw-bg-blue-100 tw-text-blue-700 hover:tw-bg-blue-100',
  pending: 'tw-bg-amber-100 tw-text-amber-700 hover:tw-bg-amber-100',
  refunded: 'tw-bg-rose-100 tw-text-rose-700 hover:tw-bg-rose-100',
  cancelled: 'tw-bg-muted tw-text-muted-foreground hover:tw-bg-muted',
};

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="tw-rounded-lg tw-border tw-border-border tw-bg-card tw-px-3 tw-py-2.5">
      <div className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
        {label}
      </div>
      <div className="tw-mt-1 tw-text-xl tw-font-bold tw-leading-none">{value}</div>
    </div>
  );
}

function ChannelClickRow({ label, clicks, sharePercent }: { label: string; clicks: number; sharePercent: number }) {
  return (
    <>
      <div className="tw-flex tw-items-center tw-gap-2">
        <span className="tw-w-20 tw-shrink-0">{label}</span>
        <div className="tw-h-1.5 tw-flex-1 tw-overflow-hidden tw-rounded-full tw-bg-muted">
          <div className="tw-h-full tw-rounded-full tw-bg-primary" style={{ width: `${sharePercent}%` }} />
        </div>
      </div>
      <span className="tw-tabular-nums">{clicks}</span>
      <span className="tw-w-10 tw-text-right tw-tabular-nums tw-text-muted-foreground">{sharePercent}%</span>
    </>
  );
}

function OrderStatusBadge({ order }: { order: VoucherPerformanceOrder }) {
  const displayStatus = resolveVoucherOrderDisplayStatus(order);
  return <Badge className={ORDER_STATUS_STYLES[displayStatus]}>{VOUCHER_ORDER_STATUS_LABELS[displayStatus]}</Badge>;
}

interface VoucherPerformanceSheetProps {
  voucher: InfluencerVoucher | null;
  onClose: () => void;
}

export function VoucherPerformanceSheet({ voucher, onClose }: VoucherPerformanceSheetProps) {
  const { data: performance, isPending } = useQuery(VOUCHER_QUERIES.performance(voucher?.code ?? ''));
  const currency = performance?.currency ?? 'USD';
  const weeklyChartData = (performance?.weekly ?? []).map((week) => ({
    weekStart: week.weekStart,
    revenue: parseAmount(week.revenue),
  }));
  const channelClickRows = performance ? buildVoucherChannelClickRows(performance) : [];

  return (
    <Sheet open={voucher !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="tw-w-[560px] tw-max-w-full tw-overflow-y-auto tw-p-0 sm:tw-max-w-[560px]">
        {voucher && (
          <div>
            <div className="tw-border-b tw-border-border tw-p-5 tw-pr-12">
              <div className="tw-flex tw-items-center tw-gap-2">
                <code className="tw-rounded tw-border tw-border-border tw-bg-muted tw-px-2 tw-py-0.5 tw-font-mono tw-text-sm tw-font-semibold">
                  {voucher.code}
                </code>
                <Badge
                  className={
                    voucher.status === 'active'
                      ? 'tw-bg-green-100 tw-text-green-700 hover:tw-bg-green-100'
                      : 'tw-bg-muted tw-text-muted-foreground hover:tw-bg-muted'
                  }
                >
                  {voucher.status === 'active' ? 'Active' : 'Disabled'}
                </Badge>
              </div>
              <p className="tw-mt-2 tw-text-sm tw-text-muted-foreground">
                @{voucher.handle} · {voucher.platform}
                {voucher.displayName ? ` · ${voucher.displayName}` : ''} · {voucher.discountPercent}% discount /{' '}
                {voucher.commissionPercent !== undefined ? `${voucher.commissionPercent}% commission` : 'program rates'} ·
                created {formatDate(voucher.createdAt)}
              </p>
            </div>

            <div className="tw-space-y-5 tw-p-5">
              <div className="tw-grid tw-grid-cols-5 tw-gap-2.5">
                <StatCard label="Uses" value={String(voucher.uses)} />
                <StatCard label="Clicks" value={String(performance?.clicks ?? voucher.clicks)} />
                <StatCard label="Orders" value={String(performance?.orderCount ?? voucher.orderCount)} />
                <StatCard
                  label="Revenue"
                  value={formatCurrency(performance?.revenueTotal ?? voucher.revenueTotal, currency)}
                />
                <StatCard
                  label="Commission"
                  value={formatCurrency(performance?.commissionTotal ?? voucher.commissionTotal, currency)}
                />
              </div>

              <div>
                <div className="tw-mb-2 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
                  Clicks by channel
                </div>
                <div className="tw-rounded-lg tw-border tw-border-border tw-px-3 tw-py-2">
                  {isPending ? (
                    <div className="tw-py-4 tw-text-center tw-text-sm tw-text-muted-foreground">Loading…</div>
                  ) : (
                    <div className="tw-grid tw-grid-cols-[1fr_auto_auto] tw-items-center tw-gap-x-3 tw-gap-y-1.5 tw-text-sm">
                      {channelClickRows.map((row) => (
                        <ChannelClickRow
                          key={row.key}
                          label={row.label}
                          clicks={row.clicks}
                          sharePercent={row.sharePercent}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <div className="tw-mb-2 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
                  Weekly revenue
                </div>
                <div className="tw-rounded-lg tw-border tw-border-border tw-p-3">
                  {isPending ? (
                    <div className="tw-flex tw-h-[160px] tw-items-center tw-justify-center tw-text-sm tw-text-muted-foreground">
                      Loading…
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={weeklyChartData}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="weekStart" tick={{ fontSize: 10 }} tickLine={false} />
                        <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={40} />
                        <Tooltip formatter={(value) => formatCurrency(Number(value), currency)} />
                        <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              <div>
                <div className="tw-mb-2 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
                  Attributed orders
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Order</TableHead>
                      <TableHead className="tw-text-right">Revenue</TableHead>
                      <TableHead className="tw-text-right">Commission</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(performance?.orders ?? []).length === 0 && !isPending && (
                      <TableRow>
                        <TableCell colSpan={5} className="tw-py-8 tw-text-center tw-text-muted-foreground">
                          No attributed orders yet.
                        </TableCell>
                      </TableRow>
                    )}
                    {(performance?.orders ?? []).map((order) => (
                      <TableRow key={order.orderId}>
                        <TableCell className="tw-text-muted-foreground">{formatDate(order.date)}</TableCell>
                        <TableCell>#{order.orderId}</TableCell>
                        <TableCell className="tw-text-right tw-tabular-nums">
                          {formatCurrency(order.revenue, currency)}
                        </TableCell>
                        <TableCell className="tw-text-right tw-tabular-nums">
                          {formatCurrency(order.commission, currency)}
                        </TableCell>
                        <TableCell>
                          <OrderStatusBadge order={order} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
